import values from 'lodash/values';
import PgExperiments from './PgExperiments';
import { OptimizelyExperiment } from '../types/optimizely';
import FeatureService from '../util/FeatureService';

// Experiments section

const AUTO_ONLY_MEDIA_ALPHA_EXPERIMENT_NAME = 'PnC Only Media Alpha';
const AUTO_ONLY_MEDIA_ALPHA_VARIANT_NAME = 'Only Media Alpha';

const HOME_DATA_HYDRATION_EXPERIMENT_NAME = 'PnC Home Data Hydration';
const HOME_DATA_HYDRATION_VARIANT_NAME = 'Home Data Hydration';

const pgExperiments = () => window.PgExperiments;

export const getActiveExperiment = (
  experimentName: string
): OptimizelyExperiment | null | undefined => {
  if (!window.optimizely || !window.optimizely.get) {
    return null;
  }

  const experimentStates = window.optimizely.get('state').getExperimentStates();

  return values(experimentStates).find(
    (exp) => exp.isActive && exp.experimentName === experimentName
  );
};

export const isVariationActive = (experimentName: string, variationName: string): boolean => {
  if (pgExperiments()) {
    const experiment = pgExperiments().getExperimentByNameAndVariant(experimentName, variationName);

    return experiment.isActive();
  }
  const activeExperiment = getActiveExperiment(experimentName);

  if (!activeExperiment) {
    return false;
  }

  return activeExperiment.variation.name === variationName;
};

export const isMultivariateActive = (experimentName: string, ...variantNames: string[]): boolean =>
  variantNames.some((variant) => isVariationActive(experimentName, variant));

/* eslint-disable no-console */
export const initPgExperiments = (): void => {
  if (typeof window !== 'undefined') {
    window.PgExperiments = new PgExperiments([
      {
        name: AUTO_ONLY_MEDIA_ALPHA_EXPERIMENT_NAME,
        variant: AUTO_ONLY_MEDIA_ALPHA_VARIANT_NAME,
      },
      {
        name: HOME_DATA_HYDRATION_EXPERIMENT_NAME,
        variant: HOME_DATA_HYDRATION_VARIANT_NAME,
      },
    ]);
    console.log(
      'PgExperiments initialized. Methods: list, activateByIndex(), deactivateByIndex(), getExperimentByNameAndVariant()'
    );
  }
};
/* eslint-enable no-console */

export const isAutoOnlyMediaAlphaActivated = () =>
  isVariationActive(AUTO_ONLY_MEDIA_ALPHA_EXPERIMENT_NAME, AUTO_ONLY_MEDIA_ALPHA_VARIANT_NAME);

export const isHomeDataHydrationActive = () =>
  isVariationActive(HOME_DATA_HYDRATION_EXPERIMENT_NAME, HOME_DATA_HYDRATION_VARIANT_NAME);

// Feature flags section

const QUOTE_ESTIMATES_TIMEOUT_FLAG_NAME = 'pnc_operations_quote-estimates-timeout';
const HFI_INBOUND_NUMBER = 'pnc_release_hfi-inbound-number';
const REMOVE_FUNNEL_QUESTIONS = 'pnc_release_remove-funnel-questions';
const UPDATE_FUNNEL_SEGMENT_EVENTS = 'pnc_release-update-funnel-segment-events';
const MEDIA_ALPHA_RENTERS = 'pnc_release_media_alpha_renters';
const RELEASE_RECAPTCHA = 'pnc_release_recaptcha';
const FORCE_LOGIN_AUTOLOGIN_USERS = 'pnc_release_force-login-autologin-users';
const ENABLE_AUTO_DATA_HYDRATION_FOR_AUTO_ONLY =
  'pnc_release_toggle-auto-data-hydration-for-auto-only';
const SHOW_INELIGIBLE_FULL_QUOTING_CALENDLY_PAGE =
  'pnc_release_ineligible-full-quoting-calendly-page';

const KIN_PARTNERSHIP = 'pnc_release_kin-partnership';
const REDFIN_ROUTING = 'pnc_release_redfin-routing';
const HIDE_EMAIL_QUOTES_BUTTON = 'pnc_release_hide-email-quotes-button';

const NEW_VENDOR_EXPERIENCE = 'pnc_release_new_vendor_experience';

export const getQuoteEstimateTimeout = () => {
  const timeout = FeatureService.getFeatureVariable(
    QUOTE_ESTIMATES_TIMEOUT_FLAG_NAME,
    'timeoutseconds',
    60
  );

  return timeout * 1000;
};

export const isReleaseRecaptchaActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(RELEASE_RECAPTCHA, userUuid, userEmail);

export const isHFIInboundNumberActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(HFI_INBOUND_NUMBER, userUuid, userEmail);

export const isRemoveFunnelQuestionsActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(REMOVE_FUNNEL_QUESTIONS, userUuid, userEmail);

export const isUpdateFunnelSegmentEventsActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(UPDATE_FUNNEL_SEGMENT_EVENTS, userUuid, userEmail);

export const isMediaAlphaRentersActive = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(MEDIA_ALPHA_RENTERS, userUuid, userEmail);

export const isForceLoginAutologinUsersActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(FORCE_LOGIN_AUTOLOGIN_USERS, userUuid, userEmail);

export const isHydrateForAutoOnlyActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(ENABLE_AUTO_DATA_HYDRATION_FOR_AUTO_ONLY, userUuid, userEmail);

export const isKinPartnershipActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(KIN_PARTNERSHIP, userUuid, userEmail);

export const isIneligibleFullQuotingCalendlyPageActivated = (
  userUuid: string,
  userEmail?: string
) => FeatureService.isActive(SHOW_INELIGIBLE_FULL_QUOTING_CALENDLY_PAGE, userUuid, userEmail);

export const isRedfinRoutingActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(REDFIN_ROUTING, userUuid, userEmail);

export const isHideEmailQuotesButtonActivated = (userUuid: string, userEmail?: string) =>
  FeatureService.isActive(HIDE_EMAIL_QUOTES_BUTTON, userUuid, userEmail);

export const isNewVendorExperienceActivated = (options: {
  userUuid: string;
  userEmail?: string;
  automatedTestUser?: boolean;
}) =>
  FeatureService.isActive(
    NEW_VENDOR_EXPERIENCE,
    options.userUuid,
    options.userEmail,
    options.automatedTestUser
  );
